// SetFieldActionRow.jsx

import React from 'react';

// Utilities
import { normalizeActionValueType } from '../utils/automationActionEditorUtils';
import {
  getAllowedValueModes,
  peopleLiteralToText,
} from 'collection/automations/utils/automationValueUtils';

// CSS
import './SetFieldActionRow.css';

// Components
import AutomationLiteralInput from '../../AutomationLiteralInput';
import Button from 'components/button/Button';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * SetFieldActionRow
 *
 * Renders one set-field action row with field, value mode, and literal editor.
 *
 * @param {object} action - Action row payload.
 * @param {object} theme - Theme object used for dynamic styling.
 * @param {array} actions - Full action list for remove-button state.
 * @param {array} fieldOptions - Available fields for set-field selection.
 * @param {Map} fieldTypeByKey - Field type lookup keyed by field key.
 * @param {Map} fieldEnumOptionsByKey - Enum option lookup keyed by field key.
 * @param {array} [baseMembers=[]] - Base members for people-literal labels.
 * @param {boolean} [allowEmailActions=true] - Whether send-email actions can be selected.
 * @param {function} updateAction - Action row updater callback.
 * @param {function} onTypeChange - Callback to switch between action types.
 * @param {function} onRemove - Callback to remove this action row.
 * @param {function} [onOpenPeoplePicker] - Opens member picker for people literals.
 *
 * @returns {ReactNode} Rendered set-field row.
 */
const SetFieldActionRow = React.memo(({
  action,
  theme,
  actions,
  fieldOptions,
  fieldTypeByKey,
  fieldEnumOptionsByKey,
  baseMembers = [],
  allowEmailActions = true,
  updateAction,
  onTypeChange,
  onRemove,
  onOpenPeoplePicker,
}) => {

  const actionFieldType = fieldTypeByKey.get(action?.fieldKey || '') || 'string';
  const allowedActionValueModes = getAllowedValueModes(actionFieldType);
  const actionValueType = normalizeActionValueType(action?.value?.type, allowedActionValueModes);
  const peopleLabel = peopleLiteralToText(action?.value?.literal, baseMembers) || 'Pick people…';

  const actionTypeOptions = [
    { value: 'set_field', label: 'Update field' },
    ...(allowEmailActions ? [{ value: 'send_email', label: 'Send email' }] : []),
  ];

  const valueModeOptions = [
    ...(allowedActionValueModes.includes('literal') ? [{ value: 'literal', label: 'Literal' }] : []),
    ...(allowedActionValueModes.includes('now') ? [{ value: 'now', label: 'Now()' }] : []),
    ...(allowedActionValueModes.includes('add') ? [{ value: 'add', label: 'Add tags' }] : []),
    ...(allowedActionValueModes.includes('remove') ? [{ value: 'remove', label: 'Remove tags' }] : []),
    ...(allowedActionValueModes.includes('null') ? [{ value: 'null', label: 'Null' }] : []),
  ];

  // ----- RENDER ----- //

  return (
    <div className="automation-modal-action-block" key={action.key}>

      {/* ROW FIVE */}
      <div className="automation-rule-row automation-rule-row-five">

        <div>
          <SelectInput
            style={{ fontSize: '12px' }}
            value="set_field"
            options={actionTypeOptions}
            onChange={(event) => {
              onTypeChange(event.target.value);
            }}
          />
        </div>

        <div>
          <SelectInput
            style={{ fontSize: '12px' }}
            value={action?.fieldKey || ''}
            placeholder="Select field..."
            options={fieldOptions.map((field) => ({
              value: field.key,
              label: field.label,
            }))}
            onChange={(event) => {
              const nextFieldKey = event.target.value;
              const nextFieldType = fieldTypeByKey.get(nextFieldKey) || 'string';
              const nextAllowedModes = getAllowedValueModes(nextFieldType);

              updateAction((entry) => {
                const normalizedValueType = normalizeActionValueType(entry?.value?.type, nextAllowedModes);

                return {
                  ...entry,
                  fieldKey: nextFieldKey,
                  value: normalizedValueType === 'literal'
                    || normalizedValueType === 'add'
                    || normalizedValueType === 'remove'
                    ? {
                      type: normalizedValueType,
                      literal: entry?.value?.literal ?? '',
                    }
                    : { type: normalizedValueType },
                };
              });
            }}
          />
        </div>

        <div>
          <SelectInput
            style={{ fontSize: '12px' }}
            value={actionValueType}
            options={valueModeOptions}
            onChange={(event) => {
              const nextType = event.target.value;
              updateAction((entry) => ({
                ...entry,
                value: nextType === 'literal' || nextType === 'add' || nextType === 'remove'
                  ? { type: nextType, literal: '' }
                  : { type: nextType },
              }));
            }}
          />
        </div>

        <div>
          {(actionValueType === 'literal' || actionValueType === 'add' || actionValueType === 'remove')
            && (
              actionFieldType === 'people' && typeof onOpenPeoplePicker === 'function' ? (
                <Button
                  label={peopleLabel}
                  height="32px"
                  fontSize="12px"
                  borderRadius="8px"
                  padding="0 10px"
                  backgroundColor={theme.panel}
                  borderColor={theme.backgroundMuted}
                  foregroundColor={theme.foreground}
                  hoverBackgroundColor={theme.backgroundHover}
                  title={peopleLabel === 'Pick people…' ? 'Choose people' : peopleLabel}
                  onClick={onOpenPeoplePicker}
                />
              ) : (
                <AutomationLiteralInput
                  fieldKey={action?.fieldKey || ''}
                  value={action?.value?.literal}
                  theme={theme}
                  fieldTypeByKey={fieldTypeByKey}
                  fieldEnumOptionsByKey={fieldEnumOptionsByKey}
                  baseMembers={baseMembers}
                  onValueChange={(nextValue) => {
                    updateAction((entry) => ({
                      ...entry,
                      value: {
                        type: actionValueType,
                        literal: nextValue,
                      },
                    }));
                  }}
                />
              )
            )}
        </div>

        <div className="automation-modal-action-tail">
          <Button
            iconKey="do_not_disturb_on"
            iconSize="16"
            foregroundColor={theme.foregroundMuted}
            backgroundColor="transparent"
            borderColor="transparent"
            hoverBackgroundColor="transparent"
            borderRadius="6px"
            height="28px"
            width="28px"
            padding="0"
            enabled={actions.length > 1}
            title="Remove action"
            onClick={onRemove}
          />
        </div>
      </div>
    </div>
  );
});

// Dev Tools
SetFieldActionRow.displayName = 'SetFieldActionRow';

export default SetFieldActionRow;
